import type {CandidateDto} from '@devmil/contracts';
import {TrendingUp} from 'lucide-react';
import {useId} from 'react';

import {formatDays, formatShortDate} from '../../lib/format.js';

const NUMERO = new Intl.NumberFormat('pt-BR', {maximumFractionDigits: 0});

/**
 * Candidatos a repetir. Não são clicáveis na prévia aprovada, então continuam
 * como conteúdo, e não como botão que não faz nada.
 *
 * O que mudou foi a natureza da lista. Ela vinha vazia do servidor, porque
 * `retentionPercent` e `mapaStarted` por peça não têm coluna nenhuma no
 * schema, e no modo ilustrativo vinha da fixture. Agora o candidato é a peça
 * que alcançou mais que a média do PRÓPRIO formato e idade, e a linha carrega a
 * evidência inteira: quanto ela fez, contra que média, sobre quantas peças, em
 * que idade e com que versão da Graph.
 *
 * A frase é escrita como OBSERVAÇÃO, e não como causa. "Alcançou 2.100 contra a
 * média de 1.240 em 3 peças" é o que foi medido; por que ela alcançou é
 * hipótese, e hipótese que a tela afirma vira experimento que ninguém rodou.
 */
export const CandidateList = ({
  candidates,
}: {
  candidates: readonly CandidateDto[];
}) => {
  const headingId = useId();

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Candidatos</h2>
        <span className="dm-illustrative">ACIMA DA MÉDIA DO FORMATO</span>
      </div>
      <div className="dm-queue">
        {candidates.length === 0 ? (
          <p className="dm-piece-meta" data-testid="metrics-no-candidate">
            Nenhuma peça passou da média do próprio formato com amostra
            suficiente.
          </p>
        ) : (
          candidates.map((candidate, index) => (
            <div
              className="dm-piece"
              key={candidate.id}
              data-testid="metrics-candidate"
              aria-label={`Candidato ${index + 1}`}
            >
              <span className="dm-piece-thumb">
                <TrendingUp aria-hidden="true" />
              </span>
              <span>
                <span className="dm-piece-title">{candidate.title}</span>
                <span className="dm-piece-meta">
                  {`Alcance ${NUMERO.format(candidate.value)} contra média ${NUMERO.format(
                    candidate.cohortAverage,
                  )} em ${candidate.cohortSize} peças · ${formatDays(
                    candidate.ageDays,
                  )} · ${formatShortDate(candidate.measuredAt)} · ${candidate.sourceRevision}`}
                </span>
              </span>
              <span className="dm-state is-approved">CANDIDATO</span>
            </div>
          ))
        )}
      </div>
    </section>
  );
};
